import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, Alert } from 'antd';
import { useLayoutConfig } from '@/components/common/ProtectedLayout';
import { KanbanBoard } from '@/components/features/kanban/KanbanBoard';
import { KanbanSkeleton } from '@/components/common/skeletons/KanbanSkeleton';
import { useKanbanBoard } from '@/hooks/useKanbanBoard';

/**
 * Kanban Board Page
 * Route: /apps/tickets/boards/:slug
 *
 * Phase 1: Display bucket columns only (read-only)
 */
export const KanbanBoardPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { setNoPadding } = useLayoutConfig();

  // Set noPadding mode for this page
  useEffect(() => {
    setNoPadding(true);
    return () => setNoPadding(false);
  }, [setNoPadding]);

  const { data: board, isLoading, error, refetch } = useKanbanBoard(slug || '');

  const handleBoardUpdate = () => {
    // Refetch board data after successful drag & drop
    refetch();
  };

  return (
    <>
      {isLoading && <KanbanSkeleton bucketCount={4} cardsPerBucket={3} />}

      {error && (
        <div style={{ padding: '20px' }}>
          <Alert
            message="Error Loading Board"
            description={
              error instanceof Error
                ? error.message
                : 'Failed to load Kanban board'
            }
            type="error"
            showIcon
          />
        </div>
      )}

      {!board && !isLoading && !error && (
        <div style={{ padding: '20px' }}>
          <Alert
            message="Board Not Found"
            description={`No board found with slug: ${slug}`}
            type="warning"
            showIcon
          />
        </div>
      )}

      {board && <KanbanBoard board={board} onBoardUpdate={handleBoardUpdate} />}
    </>
  );
};
